import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { HoldTypeResponseDto } from '@planning/app/api'
import holdTypeService from '@planning/services/holdTypeService'
import { useTranslate } from '@tolgee/react'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface Props {
  value: number | null
  onChange: (holdType: HoldTypeResponseDto | null) => void
  error?: boolean
  required?: boolean
  disabled?: boolean
  onBlur?: () => void
}

export const HoldTypeAutocomplete = ({ value, onChange, error, required, disabled, onBlur }: Props) => {
  const { t } = useTranslate()
  const [holdTypes, setHoldTypes] = useState<HoldTypeResponseDto[]>([])
  const [loading, setLoading] = useState(false)

  const fetchHoldTypes = useCallback(async () => {
    setLoading(true)
    const types = await holdTypeService.get(false)
    setHoldTypes(types.filter(ht => ht.isActive))
    setLoading(false)
  }, [])

  useEffect(() => {
    if (value !== null && holdTypes.length === 0 && !loading) fetchHoldTypes()
  }, [value, holdTypes.length, loading, fetchHoldTypes])

  const selectedValue = useMemo(
    () => holdTypes.find(ht => ht.id === value) ?? null,
    [holdTypes, value],
  )

  return (
    <Autocomplete
      options={holdTypes}
      value={selectedValue}
      loading={loading}
      disabled={disabled}
      disableClearable={false}
      getOptionLabel={ht => ht.name}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      onOpen={() => { if (!loading) fetchHoldTypes() }}
      onChange={(_, newValue) => onChange(newValue)}
      renderInput={params => (
        <TextField
          {...params}
          label={t('holdType', 'Hold type')}
          required={required}
          error={error}
          helperText={error ? t('fieldIsRequired', 'Field is required.') : ''}
          onBlur={onBlur}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={16} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  )
}
